import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { DEFAULT_LANGUAGE, LANGUAGE_CODES, isRTL, translate } from "../lib/i18n";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "pq_lang";

const LanguageCtx = createContext(null);

function readInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && LANGUAGE_CODES.includes(saved)) return saved;
  // Fall back to browser language when it matches a supported code.
  const nav = (navigator.language || "").slice(0, 2).toLowerCase();
  if (LANGUAGE_CODES.includes(nav)) return nav;
  return DEFAULT_LANGUAGE;
}

function applyDocumentDir(lang) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("lang", lang);
  document.documentElement.setAttribute("dir", isRTL(lang) ? "rtl" : "ltr");
}

export function LanguageProvider({ children }) {
  const { user, refresh } = useAuth();
  const [lang, setLangState] = useState(readInitialLang);

  // Adopt the language stored on the user profile once auth resolves.
  useEffect(() => {
    if (user?.language && LANGUAGE_CODES.includes(user.language) && user.language !== lang) {
      setLangState(user.language);
      localStorage.setItem(STORAGE_KEY, user.language);
    }
    // Intentionally excluding `lang` — we only want to adopt on user change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  // Reflect the language in the <html> element (dir, lang).
  useEffect(() => {
    applyDocumentDir(lang);
  }, [lang]);

  const setLang = useCallback(async (next) => {
    if (!LANGUAGE_CODES.includes(next) || next === lang) return;
    setLangState(next);
    localStorage.setItem(STORAGE_KEY, next);
    // Persist to the account when signed in — best-effort, don't block the UI.
    if (user?.id) {
      try {
        await api.patch("/auth/me", { language: next });
        await refresh();
      } catch {
        /* ignore — local preference still applies */
      }
    }
  }, [lang, user?.id, refresh]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t: (key, vars) => translate(lang, key, vars),
    rtl: isRTL(lang),
  }), [lang, setLang]);

  return <LanguageCtx.Provider value={value}>{children}</LanguageCtx.Provider>;
}

export const useLang = () => {
  const ctx = useContext(LanguageCtx);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider>");
  return ctx;
};
